import React, { Fragment } from 'react';
import get from 'lodash/get';
import { noop } from 'utils/noop';
import ReactJson from 'react-json-view';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { Tooltip } from 'ui/tooltip';
import Link from 'core/link';
import { getCellWidth } from './helpers';
import ActionCell from './ActionCell';
import * as t from './cellTypes';
import { CellWrapper, CellCSSContainer } from './Cell.styled';
import { shouldComponentUpdate } from './Cell.utils';

// type Props = {
//   actionCell: any,
//   /** Text align of cell. */
//   align: string,
//   /** Vertical alignment of text within cell. */
//   verticalAlign: string,
//   /** If cell is a checkbox, determines checked state. */
//   checked: boolean,
//   /** @private */
//   children: any,
//   /** An additional table cell class. */
//   className: string,
//   /** @private */
//   docked: boolean,
//   /**
//    * Function to run to format display value.
//    *
//    * ```
//    * formatter(value, rowData) => {}
//    * ```
//    * @param { String } value: The value of the cell.
//    * @param { Object } rowData: The data object of the row.
//    * @returns { React.Component } The cell content.
//    */
//   formatter: (value: string, rowData: {}) => *,
//   /**
//    * Formats the url of the link.
//    *
//    * ```
//    * formatUrl(url, rowData) => {}
//    * ```
//    * @param { String } url: The current url string.
//    * @param { Object } rowData: The data object of the row.
//    * @returns { Boolean } The url.
//    */
//   formatUrl: (url: string, rowData: {}) => string,
//   /**
//    * If cell type is custom, return the contents of the custom cell.
//    *
//    * ```
//    * getCellContent(e, multiSelectProps) => {}
//    * ```
//    * @param { Object } props: The props of the cell.
//    * @returns { React.Component } The cell content.
//    */
//   getCellContent: (props: {}) => *,
//   /**
//    * Gets the toggle state of the toggle.
//    *
//    * ```
//    * getToggleValue(toggleValue, rowData) => {}
//    * ```
//    * @param { String } toggleValue: The value of the toggle given fronm the value prop.
//    * @param { Object } rowData: The data object of the row.
//    * @returns { Boolean } The toggle state.
//    */
//   getToggleValue: (toggleValue: string, rowData: {}) => boolean,
//   /** If cell contains a dropdown. Applies styles to open dropdown correctly. */
//   hasDropdown: boolean,
//   /** Position of the cell. */
//   index: number,
//   /** Optional react router link */
//   link: ?boolean,
//   /** Max width of column. (Pixels) */
//   maxWidth: number,
//   /** Min width of column. (Pixels) */
//   minWidth: number,
//   /** Name of the column the cell belongs to. */
//   name: string,
//   /** If not link, callback function when cell is clicked. */
//   onClick: () => {},
//   /** If url is provided, link opens in new window. */
//   openInNewContext: boolean,
//   /** Optional custom props for specific cell */
//   otherProps: ?Object,
//   /** Data of the previous row */
//   previousRowData: {},
//   /** Stylize link as primary table link. */
//   primaryAction: boolean,
//   /** Callback that renders custom cell type and returns React component */
//   renderCell: (props: {}) => *,
//   /** Data of the row */
//   rowData: {},
//   /** If the row containing the cell is selected. */
//   rowSelected: boolean,
//   /** The key of the row value. */
//   rowValueKey: string,
//   /** Display cell value as title. */
//   showTitle: boolean,
//   /** Contents of tooltip if hovered. */
//   tooltip: *,
//   /** The cell type. */
//   type: string,
//   /** URL to go to if given or show as link. Use formatUrl() to get correct url path */
//   url: string | boolean,
//   /** The value of the cell. */
//   value: *,
//   /** Width of the cell. Overrides minWidth and maxWidth. */
//   width: number,
// };

export default class Cell extends React.Component {
  static defaultProps = {
    align: 'left',
    verticalAlign: 'top',
    formatter: (text) => text,
    formatUrl: (url) => url,
    getCellContent: noop,
    getToggleValue: noop,
    onClick: noop,
    name: '',
    previousRowData: {},
    rowData: {},
    type: t.TEXT,
  };

  shouldComponentUpdate = (nextProps) => shouldComponentUpdate(nextProps, this.props);

  /**
   * Generates a checkbox cell.
   * @returns { React.Component } The generated checkbox cell.
   */
  getCheckBoxCell = () => {
    const { checked, onClick, value } = this.props;
    return (
      <SemanticUI.Checkbox
        data-test="core-table-checkbox-cell"
        checked={checked}
        onChange={onClick}
        value={value}
      />
    );
  };

  /**
   * Generates an enabled circle cell that displays a green
   * checkmark when the enabled value is true or a gray
   * minus when the value is false.
   * @returns { React.Component } The generated enabled cell.
   */
  getEnabledCell = () => {
    const { value, otherProps } = this.props;
    const enabledIcon = (
      <SemanticUI.Icon
        name={value ? 'check circle outline' : 'minus circle'}
        className={value ? 'cssEnabled' : 'cssDisabled'}
        data-test={get(otherProps, 'dataTest')}
      />
    );
    if (otherProps) {
      const { enabledCellTextTruthy, enabledCellTextFalsy } = otherProps;
      if (enabledCellTextTruthy && enabledCellTextFalsy) {
        return (
          <Fragment>
            {enabledIcon}
            {value ? otherProps.enabledCellTextTruthy : otherProps.enabledCellTextFalsy}
          </Fragment>
        );
      }
    }
    return enabledIcon;
  };

  /**
   * Generates the group cell.
   * @returns { React.Component } The group cell.
   */
  getGroupCell = () => {
    const { previousRowData, rowValueKey, value } = this.props;
    const previousGroup = previousRowData[rowValueKey];
    if (value !== previousGroup) {
      return value;
    }
    return '';
  };

  /**
   * Callback function when toggle is clicked.
   * @param { Object } e: The click event.
   * @param { Object } toggleParams: Updated params of the toggle with new toggle (checked) state.
   * @returns { void }
   */
  handleToggleClick = (e, { checked }) => {
    const { onClick, rowData } = this.props;
    onClick(e, { checked, rowData });
  };

  /**
   * Generates a toggle cell.
   * @returns { React.Component } The generated toggle cell.
   */
  getToggleCell = () => {
    const { getToggleValue, rowData, value } = this.props;
    return (
      <SemanticUI.Checkbox
        data-test="core-table-toggle-cell"
        checked={getToggleValue(value, rowData)}
        onChange={this.handleToggleClick}
        toggle
      />
    );
  };

  /**
   * Generates a text cell.
   * @returns { React.Component } The generated text cell.
   */
  getTextCell = () => {
    const {
      formatter,
      formatUrl,
      name,
      openInNewContext,
      otherProps,
      rowData,
      showTitle,
      tooltip,
      link,
      primaryAction,
      url,
      value,
    } = this.props;
    const textProps = { name };
    const displayValue = formatter(value, rowData);
    let TextComponentClass = 'div';
    if (link && rowData.link && get(otherProps, 'isLink')) {
      TextComponentClass = Link;
      textProps.to = rowData.link;
      textProps.tableLink = true;
      textProps.tableLinkPrimary = primaryAction;
    }
    if (get(otherProps, 'isJson')) {
      TextComponentClass = ReactJson;
      textProps.src = JSON.parse(value);
      Object.keys(get(otherProps, 'rjvProps', [])).forEach((key) => {
        textProps[key] = otherProps.rjvProps[key];
      });
      textProps.style = otherProps.style;
    }
    if (url) {
      const formattedUrl = formatUrl(url, rowData);
      TextComponentClass = Link;
      textProps.to = formattedUrl;
      textProps.tableLink = true;
      textProps.tableLinkPrimary = primaryAction;
      textProps.openInNewContext = openInNewContext;
    }
    const textComponent = (
      <TextComponentClass
        title={showTitle ? displayValue : undefined}
        {...textProps}
        className={get(otherProps, 'style') ? otherProps.style : undefined}
        data-test={get(otherProps, 'dataTest') ? otherProps.dataTest : undefined}
      >
        {displayValue}
      </TextComponentClass>
    );
    if (tooltip) {
      return <Tooltip tooltipContent={tooltip}>{textComponent}</Tooltip>;
    }
    return textComponent;
  };

  /**
   * Generates the contents of a cell.
   * @returns { React.Component } The generated cell.
   */
  getCellContent = () => {
    const { getCellContent: getCell, type } = this.props;
    switch (type) {
      case t.GROUP:
        return this.getGroupCell();
      case t.CHECKBOX:
        return this.getCheckBoxCell();
      case t.CUSTOM:
        return getCell(this.props);
      case t.ENABLED:
        return this.getEnabledCell(this.props);
      case t.TOGGLE:
        return this.getToggleCell(this.props);
      case t.TEXT:
      default:
        return this.getTextCell();
    }
  };

  /**
   * getActionCell
   * Generates the action cell
   * @returns { React.Component } The action cell.
   */
  getActionCell = () => {
    const { actionCell, rowData } = this.props;
    if (actionCell) {
      return <ActionCell {...actionCell} rowData={rowData} />;
    }
    return undefined;
  };

  render() {
    const {
      align,
      verticalAlign,
      className,
      docked,
      hasDropdown,
      maxWidth,
      minWidth,
      renderCell,
      type,
      width,
      otherProps,
    } = this.props;
    const actionCell = this.getActionCell();

    if (type === t.CUSTOM_CELL) {
      return renderCell(this.props);
    }
    const classNames = `${className} tableCell ${hasDropdown ? 'cssCellDropdown' : ''} ${
      type === t.GROUP ? 'cssGroupCell' : ''
    }`;
    return (
      <CellCSSContainer
        $hasActions={actionCell}
        className={classNames}
        data-actions={!!actionCell || undefined}
        data-role="cell"
        data-docked={docked}
        data-shrink={get(otherProps, 'shrink')}
        data-checkbox={type === t.CHECKBOX}
        style={{
          textAlign: align,
          verticalAlign,
          ...getCellWidth(width, minWidth, maxWidth),
        }}
      >
        {actionCell && (
          <CellWrapper>
            {actionCell}
            {this.getCellContent()}
          </CellWrapper>
        )}
        {!actionCell && this.getCellContent()}
      </CellCSSContainer>
    );
  }
}
